// SPDX-FileCopyrightText: 2026 Igor Brkic <igor@hyperglitch.com>
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Repository URL parsing.
 *
 * Accepts what people actually paste: a repository home page, a deep link to a
 * file or a commit, an `ssh://`/`git@` remote, or a bare `owner/repo` for
 * GitHub. The provider for a known host is decided here; unknown hosts are
 * left undecided and probed later.
 */

import { badUrl } from './errors.ts';
import type { ProviderId, RepoSource } from './types.ts';

const KNOWN_HOSTS: Record<string, ProviderId> = {
  'github.com': 'github',
  'www.github.com': 'github',
  'gitlab.com': 'gitlab',
  'www.gitlab.com': 'gitlab',
  'codeberg.org': 'gitea',
  'gitea.com': 'gitea',
};

/**
 * Path segments GitLab and Gitea insert between the project path and a ref,
 * which must be stripped to recover the repository itself.
 */
const PATH_MARKERS = new Set(['-', 'blob', 'tree', 'raw', 'commit', 'commits', 'src', 'blame', 'releases', 'tags', 'branches']);

export interface ParsedUrl {
  origin: string;
  host: string;
  /** Undefined for hosts we do not recognise; resolved by probing. */
  provider: ProviderId | undefined;
  owner: string;
  repo: string;
  /** A file path if the URL pointed at one. */
  path?: string;
  /** A ref if the URL pointed at one. */
  ref?: string;
}

export function parseRepoUrl(input: string): ParsedUrl {
  const raw = input.trim();
  if (raw === '') throw badUrl(input);

  // `owner/repo` on its own is taken as GitHub, which is what people mean.
  if (/^[\w.-]+\/[\w.-]+$/.test(raw)) {
    return {
      origin: 'https://github.com',
      host: 'github.com',
      provider: 'github',
      owner: raw.split('/')[0]!,
      repo: stripGit(raw.split('/')[1]!),
    };
  }

  const normalised = normaliseScheme(raw);
  let url: URL;
  try {
    url = new URL(normalised);
  } catch {
    throw badUrl(input);
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') throw badUrl(input);

  const segments = url.pathname.split('/').filter((s) => s !== '');
  if (segments.length < 2) throw badUrl(input);

  const provider = KNOWN_HOSTS[url.hostname];

  // Everything up to the first structural marker is the project path. GitLab
  // nests groups arbitrarily deep, so this cannot be a fixed two segments.
  let end = segments.findIndex((s) => PATH_MARKERS.has(s));
  if (end < 2) end = segments.length;
  const projectSegments = segments.slice(0, end).map(stripGit);
  if (projectSegments.length < 2) throw badUrl(input);

  const repo = projectSegments[projectSegments.length - 1]!;
  const owner = projectSegments.slice(0, -1).join('/');

  const parsed: ParsedUrl = {
    origin: url.origin,
    host: url.hostname,
    provider,
    owner,
    repo,
  };

  // Recover a ref and file path from a deep link, so pasting a link to the
  // board lands the user on that board.
  const rest = segments.slice(end).filter((s) => s !== '-');
  const marker = rest[0];
  if (marker !== undefined && ['blob', 'tree', 'raw', 'src', 'commit', 'commits', 'blame'].includes(marker)) {
    let tail = rest.slice(1);
    // Gitea writes /src/branch/<name>/... and /src/commit/<sha>/...
    if (tail[0] === 'branch' || tail[0] === 'commit' || tail[0] === 'tag') tail = tail.slice(1);
    if (tail.length > 0) {
      parsed.ref = decodeURIComponent(tail[0]!);
      const filePath = tail.slice(1).map((s) => decodeURIComponent(s)).join('/');
      if (filePath !== '') parsed.path = filePath;
    }
  }

  return parsed;
}

function normaliseScheme(raw: string): string {
  // git@host:owner/repo.git
  const scp = /^(?:ssh:\/\/)?(?:[\w.-]+@)([^/:]+):(.+)$/.exec(raw);
  if (scp && !raw.startsWith('http')) return `https://${scp[1]}/${scp[2]}`;
  if (/^(git|ssh):\/\//.test(raw)) return raw.replace(/^(git|ssh):\/\//, 'https://').replace(/^https:\/\/[^@]+@/, 'https://');
  if (/^https?:\/\//.test(raw)) return raw;
  // Any other explicit scheme is not something we can read; prepending https://
  // would turn `ftp://host/a/b` into a URL that parses but means nothing.
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(raw)) return raw;
  return `https://${raw}`;
}

function stripGit(segment: string): string {
  return segment.replace(/\.git$/i, '');
}

export function toSource(parsed: ParsedUrl, provider: ProviderId): RepoSource {
  return {
    provider,
    origin: parsed.origin,
    owner: parsed.owner,
    repo: parsed.repo,
    label: `${parsed.host}/${parsed.owner}/${parsed.repo}`,
  };
}
